import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from "@mui/material";
import {
  Send,
  CheckCircle,
  BugReport,
  Feedback as FeedbackIcon,
  ArrowBack,
  Email as EmailIcon,
  Build,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTheme as useCustomTheme } from "../../theme/ThemeProvider";

const FeedbackForm = ({ type, toolName }) => {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError(null);
    setSubmitting(true);

    const payload = {
      type,
      message,
      tool: toolName,
      email,
      metadata: {
        userAgent: navigator.userAgent,
        pageURL: window.location.href,
      },
    };

    try {
      const API_BASE_URL = "http://localhost:3000";
      const response = await axios.post(`${API_BASE_URL}/api/provide-feedback`, payload);
      if (response.status === 201) {
        setSuccess(true);
        setMessage("");
        setEmail("");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Failed to submit feedback. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const isBugReport = type === "bug";

  return (
    <Box
      sx={{
        minHeight: "80vh",
        py: 8,
        background: darkMode
          ? "linear-gradient(180deg, #1a202c 0%, #2d3748 100%)"
          : "linear-gradient(180deg, #f7fafc 0%, #e2e8f0 100%)",
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              borderRadius: 3,
              background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
              mb: 3,
            }}
          >
            {isBugReport ? (
              <BugReport sx={{ fontSize: 40, color: "white" }} />
            ) : (
              <FeedbackIcon sx={{ fontSize: 40, color: "white" }} />
            )}
          </Box>
          <Typography
            variant={isMobile ? "h3" : "h2"}
            component="h1"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {isBugReport ? "Report a Bug" : "Share Your Feedback"}
          </Typography>
          <Typography variant="h6" sx={{ color: "text.secondary", maxWidth: 600, mx: "auto" }}>
            {isBugReport
              ? "Help us improve by reporting any issues you encounter"
              : "We'd love to hear your thoughts and suggestions"}
          </Typography>
        </Box>

        {/* Success Message */}
        {success && (
          <Card
            sx={{
              mb: 4,
              background: darkMode
                ? alpha("#10b981", 0.1)
                : alpha("#10b981", 0.08),
              border: `2px solid ${alpha("#10b981", 0.3)}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <CheckCircle sx={{ fontSize: 60, color: "#10b981", mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#10b981", mb: 1 }}>
                Thank You!
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Your {isBugReport ? "bug report" : "feedback"} has been submitted successfully.
                We'll review it and get back to you soon.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 4,
              borderRadius: 2,
              "& .MuiAlert-icon": {
                fontSize: 28,
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Form */}
        {!success && (
          <Card
            sx={{
              background: darkMode
                ? "linear-gradient(135deg, #2d3748 0%, #1a202c 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              borderRadius: 4,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {/* Tool Name */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
                    >
                      Tool
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 2,
                        borderRadius: 2,
                        background: darkMode ? alpha("#fff", 0.03) : alpha("#6366f1", 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    >
                      <Build sx={{ color: "primary.main" }} />
                      <Typography sx={{ fontWeight: 600 }}>{toolName}</Typography>
                    </Box>
                  </Box>

                  {/* Email */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your-email@example.com"
                      disabled={submitting}
                      InputProps={{
                        startAdornment: (
                          <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: darkMode ? alpha("#fff", 0.05) : "white",
                          "&:hover": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Message */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
                    >
                      {isBugReport ? "Bug Description" : "Your Feedback"}
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={8}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={
                        isBugReport
                          ? "Please describe the bug in detail, including steps to reproduce it..."
                          : "Share your thoughts, suggestions, or ideas..."
                      }
                      disabled={submitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: darkMode ? alpha("#fff", 0.05) : "white",
                          "&:hover": {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "primary.main",
                            },
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    endIcon={
                      submitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Send />
                      )
                    }
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
                      boxShadow: `0 8px 24px ${alpha("#6366f1", 0.3)}`,
                      "&:hover": {
                        background: "linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 28px ${alpha("#6366f1", 0.4)}`,
                      },
                      "&:disabled": {
                        background: "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
                        color: "white",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Back to Home Button */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBack />}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "primary.main",
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default FeedbackForm;
